import { action, computed, observable, makeAutoObservable } from 'mobx';
import { FormInputState, FormModel, ResultValue, group, Result } from 'src_common/common/mobx-utils/Form';
import { InputStatus } from 'src/components/Input/InputStatus';
import { TextareaStatus } from 'src/components/FormComponents/Textarea/TextareaStatus';
import {
    validateRequire,
    validateListOfNumbersWithLimit,
    validateSpecialCharactersInUrl,
    validateSpaces,
    validateExpectNumber,
} from 'src/api/utils/formValidators';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { ParamsType } from 'src/api_openapi/generated/openapi_website_cms_createSpecialSports';
import { SpecialSportsModelType } from 'src/api/config/special_sports/getSpecialSports';
import { apiCommon } from 'src/api/ApiCommon';
import * as t from 'io-ts';
import { arrayMove } from 'react-movable';

import { createGuard } from 'src_common/common/createGuard';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { CompetitionModel } from 'src_common/common/websocket2/models/CompetitionModel';
import { SdkStaff, SportsListType } from 'src/AppState/sdk/staff';

const SpecialSportsFilterIO = t.interface({
    slug: t.string,
    label: t.string,
    displayOrder: t.number,
    competitionIds: t.array(t.number),
});

export type SpecialSportsFilterType = t.TypeOf<typeof SpecialSportsFilterIO>;

export const isSpecialSportFilter = createGuard(SpecialSportsFilterIO);

const SpecialSportsIO = t.interface({
    competitionIds: t.array(t.number),
    filters: t.array(SpecialSportsFilterIO),
});

export type SpecialSportsDetailsType = t.TypeOf<typeof SpecialSportsIO>;

const isSpecialSports = createGuard(SpecialSportsIO);

type SpecialSportsBodyType = ParamsType['body'];

export interface SpecialSportsType {
    slug: string;
    label: string;
    is_active: boolean;
    competitionIds: string;
    sport_type: string;
}

interface SpecialSportsLoading {
    type: 'loading';
}

interface SpecialSportsReady {
    type: 'ready';
    value: Array<SpecialSportsModelType>;
}

interface SpecialSportsError {
    type: 'error';
    value: string;
}

export type SpecialSportsContentResponse = SpecialSportsLoading | SpecialSportsReady | SpecialSportsError;
type SportTypeType = 'existing-sport' | 'new-sport';

interface SportTypeOptionType {
    key: SportTypeType;
    name: string;
}

interface SportListOptionType {
    key: string;
    name: string;
}

export class SpecialSportsState {
    @observable private readonly specialSportsListResource: Resource<Array<SpecialSportsModelType>>;
    @observable private readonly allSports: Resource<SportsListType>;
    @observable public marketsWithCustomSelectionsIdList: Array<number> = [];
    @observable public isEditMode: boolean = false;
    @observable public isShowMarketFiltersFutureFlag: boolean = false;
    @observable private inEditSpecialSports: SpecialSportsModelType | null = null;
    @observable public filtersMap: Map<string, SpecialSportsFilterType>;
    @observable public activeFilter: string | null = null;
    @observable public competitionModel: CompetitionModel | null = null;

    public readonly slugModel: FormModel<string>;
    public readonly slug: FormInputState<string>;
    public readonly slugStatus: InputStatus;
    public readonly labelModel: FormModel<string>;
    public readonly label: FormInputState<string>;
    public readonly labelStatus: InputStatus;
    public readonly isActiveModel: FormModel<boolean>;
    public readonly isActive: FormInputState<boolean>;
    public readonly isActiveStatus: InputStatus;
    public readonly competitionIdsModel: FormModel<string>;
    public readonly competitionIds: FormInputState<string>;
    public readonly competitionIdsStatus: TextareaStatus;
    public readonly sportTypeModel: FormModel<string>;
    public readonly sportType: FormInputState<SportTypeType>;
    public readonly sportTypeStatus: InputStatus;
    public readonly filterNameModel: FormModel<string>;
    public readonly filterName: FormInputState<string>;
    public readonly filterNameStatus: InputStatus;
    public readonly competitionIdModel: FormModel<number | null>;
    public readonly competitionId: FormInputState<string>;
    public readonly competitionIdStatus: InputStatus;

    private readonly form: FormModel<SpecialSportsType>;

    constructor(
        private readonly modelsState: ModelsState,
        private readonly sdkStaff: SdkStaff
    ) {
        this.filtersMap = new Map();

        this.specialSportsListResource = new Resource(async (): Promise<Array<SpecialSportsModelType>> => {
            return await apiCommon.getSpecialSports.run({});
        });

        this.allSports = new Resource(async (): Promise<SportsListType> => {
            return await this.sdkStaff.getAllSports();
        });

        this.slug = new FormInputState('', false);
        this.slugModel = this.slug.map(validateRequire).map(validateSpecialCharactersInUrl).map(validateSpaces);
        this.slugStatus = new InputStatus(this.slugModel);

        this.label = new FormInputState('', false);
        this.labelModel = this.label.toModel();
        this.labelStatus = new InputStatus(this.labelModel);

        this.sportType = new FormInputState('existing-sport', false);
        this.sportTypeModel = this.sportType.map(validateRequire);
        this.sportTypeStatus = new InputStatus(this.sportTypeModel);

        this.filterName = new FormInputState('', false);
        this.filterNameModel = this.filterName.map(validateRequire);
        this.filterNameStatus = new InputStatus(this.filterNameModel);

        this.competitionId = new FormInputState('', false);
        this.competitionIdModel = this.competitionId.map(validateExpectNumber);
        this.competitionIdStatus = new InputStatus(this.competitionIdModel);

        this.isActive = new FormInputState(false, false);
        this.isActiveModel = this.isActive.toModel();
        this.isActiveStatus = new InputStatus(this.isActiveModel);

        this.competitionIds = new FormInputState('', false);
        this.competitionIdsModel = this.competitionIds.map(validateListOfNumbersWithLimit(1000));
        this.competitionIdsStatus = new TextareaStatus(this.competitionIdsModel);

        this.form = group({
            slug: this.slugModel,
            label: this.labelModel,
            is_active: this.isActiveModel,
            competitionIds: this.competitionIdsModel,
            sport_type: this.sportTypeModel,
        }).map((item: SpecialSportsType): Result<SpecialSportsType> => {
            return new ResultValue({
                slug: item.slug,
                label: item.label,
                is_active: item.is_active,
                competitionIds: item.competitionIds,
                sport_type: item.sport_type,
            });
        });
        makeAutoObservable(this);
    }

    @computed private get prepareDetails(): SpecialSportsDetailsType {
        let competitionIds: Array<number> = [];
        const idsList = this.competitionIdsModel.result;
        if (idsList instanceof ResultValue && idsList.value !== '') {
            competitionIds = idsList.value
                .split(',')
                .map((elem) => Number(elem))
                .filter((elem) => !isNaN(elem));
        }

        return {
            competitionIds,
            filters: this.filtersList,
        };
    }

    @action public resetForm = (): void => {
        this.form.reset();
        this.isEditMode = false;
        this.inEditSpecialSports = null;
        this.filtersMap = new Map();
    };

    @action private handleCreateApiCall = async (specialSports: SpecialSportsBodyType): Promise<number | null> => {
        try {
            return await apiCommon.postCreateSpecialSports.run({ data: specialSports });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @action public handleCreate = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;
        if (result instanceof ResultValue) {
            const body = {
                ...result.value,
                details: this.prepareDetails,
            };

            const submitResult = await this.handleCreateApiCall(body);

            if (submitResult !== null) {
                await this.specialSportsListResource.refresh();
                this.resetForm();
            } else {
                console.log('Error');
            }
        }
    };

    @action public handleReset = (): void => {
        if (this.isEditMode && this.inEditSpecialSports !== null) {
            this.setEditEventDetails(this.inEditSpecialSports);
        } else {
            this.resetForm();
        }
    };

    @action public setEditEventDetails = async (specialSports: SpecialSportsModelType): Promise<void> => {
        this.inEditSpecialSports = specialSports;
        this.slug.setValue(specialSports.slug ?? '');
        this.label.setValue(specialSports.label ?? '');
        this.isActive.setValue(specialSports.is_active ?? '');

        if (specialSports.sport_type === 'existing-sport' || specialSports.sport_type === 'new-sport') {
            this.sportType.setValue(specialSports.sport_type ?? '');
        }

        if (isSpecialSports(specialSports.details)) {
            this.competitionIds.setValue(specialSports.details.competitionIds.toString());
            this.filtersMap = new Map();
            for (const filter of specialSports.details.filters) {
                this.filtersMap.set(filter.slug, filter);
            }
        }
    };

    @action public handleEdit = async (): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result instanceof ResultValue && this.inEditSpecialSports !== null) {
            const payload = {
                ...result.value,
                details: this.prepareDetails,
            };

            const submitResult = await this.handleEditApiCall(payload, this.inEditSpecialSports.id);

            if (submitResult !== null) {
                await this.specialSportsListResource.refresh();
                this.resetForm();
            } else {
                console.log('Error');
            }
        }
    };

    @action private handleEditApiCall = async (
        specialSports: SpecialSportsBodyType,
        id: number
    ): Promise<number | null> => {
        try {
            return await apiCommon.putUpdateSpecialSports.run({ data: specialSports, id });
        } catch (error) {
            console.error(error);
            return null;
        }
    };

    @computed private get specialSportsListResponse(): SpecialSportsContentResponse {
        const specialSports = this.specialSportsListResource.get();
        if (specialSports.type === 'ready' && specialSports.value !== null) {
            return {
                type: 'ready',
                value: specialSports.value,
            };
        }
        if (specialSports.type === 'loading') {
            return {
                type: 'loading',
            };
        }

        return {
            type: 'error',
            value: 'Something went wrong with fetching event details info',
        };
    }

    @computed public get specialSportsList(): Array<SpecialSportsModelType> {
        if (this.specialSportsListResponse.type === 'ready') {
            return this.specialSportsListResponse.value;
        }
        return [];
    }

    @computed public get isLoading(): boolean {
        return this.specialSportsListResponse.type === 'loading';
    }

    @action public setEditMode = (value: boolean): void => {
        this.isEditMode = value;
    };

    @action public handleRemoveApiCall = async (id: number): Promise<void> => {
        const removeResult = await apiCommon.deleteSpecialSports.run({ id });

        if (removeResult) {
            await this.specialSportsListResource.refresh();
        } else {
            console.log('Error');
        }
    };

    @action public setActiveFlag = (specialSports: SpecialSportsModelType): void => {
        const payload: SpecialSportsModelType = {
            ...specialSports,
            is_active: !specialSports.is_active,
        };

        this.handleEditActiveFlags(payload);
    };

    @action private handleEditActiveFlags = async (payload: SpecialSportsModelType): Promise<void> => {
        const submitResult = await this.handleEditApiCall(payload, payload.id);

        if (submitResult !== null) {
            await this.specialSportsListResource.refresh();
            this.resetForm();
        } else {
            console.log('Error');
        }
    };

    public get sportTypeOptions(): Array<SportTypeOptionType> {
        return [
            {
                key: 'existing-sport',
                name: 'Add to existing sport',
            },
            {
                key: 'new-sport',
                name: 'Add new sport',
            },
        ];
    }

    @computed public get iaAddToExistingSportSelected(): boolean {
        const sportTypeResult = this.sportTypeModel.result;
        if (sportTypeResult instanceof ResultValue) {
            return sportTypeResult.value === 'existing-sport';
        }

        return true;
    }

    @action public handleAddFilter = (): void => {
        const result = this.filterNameModel.result;

        if (result instanceof ResultValue) {
            const slug = result.value.toLowerCase().split(' ').join('-');

            const filterItem = this.filtersMap.get(slug) ?? null;

            if (filterItem !== null) {
                return;
            }

            this.filtersMap.set(slug, {
                slug: slug,
                label: result.value,
                displayOrder: this.filtersMap.size + 1,
                competitionIds: [],
            });

            this.filterNameModel.reset();
        }
    };

    @computed public get filtersList(): Array<SpecialSportsFilterType> {
        return Array.from(this.filtersMap.values());
    }

    @action handleFiltersOrder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        const filtersSlugList = arrayMove(this.filtersList, oldIndex, newIndex);

        const tempMap = new Map();
        const tempFilters = filtersSlugList.map((filter, index) => ({ ...filter, displayOrder: index + 1 }));
        for (const filterItem of tempFilters) {
            tempMap.set(filterItem.slug, filterItem);
        }

        this.filtersMap = tempMap;
    };

    @action public onActiveFilter = (slug: string): void => {
        if (this.activeFilter === slug) {
            this.activeFilter = null;
        } else {
            this.activeFilter = slug;
        }

        this.competitionIdModel.reset();
    };

    @action public onRemoveFilter = (slug: string): void => {
        this.filtersMap.delete(slug);

        if (this.activeFilter === slug) {
            this.activeFilter = null;
        }
    };

    @computed public get competitionIdValue(): number | null {
        const result = this.competitionIdModel.result;

        if (result instanceof ResultValue) {
            return result.value;
        }

        return null;
    }

    @action public handleAddCompetition = (): void => {
        if (this.competitionIdValue !== null) {
            const competitionModel = this.modelsState.getCompetitionModel(this.competitionIdValue);
            if (competitionModel !== null && this.activeFilter !== null) {
                const filterItem = this.filtersMap.get(this.activeFilter) ?? null;
                if (filterItem !== null) {
                    const competitions = new Set(filterItem.competitionIds);
                    competitions.add(competitionModel.id);

                    this.filtersMap.set(this.activeFilter, {
                        ...filterItem,
                        competitionIds: Array.from(competitions),
                    });

                    this.competitionIdModel.reset();
                }
            }
        }
    };

    @action public handleDeleteCompetition = (slug: string, competitionId: number): void => {
        const filterItem = this.filtersMap.get(slug) ?? null;
        if (filterItem !== null) {
            this.filtersMap.set(slug, {
                ...filterItem,
                competitionIds: filterItem.competitionIds.filter((elem) => elem !== competitionId),
            });

            this.competitionIdModel.reset();
        }
    };

    @computed public get competitionName(): string | null {
        if (this.competitionIdValue !== null) {
            const competitionModel = this.modelsState.getCompetitionModel(this.competitionIdValue);

            if (competitionModel !== null) {
                return competitionModel.name;
            }
        }

        return null;
    }

    @computed get sportList(): SportsListType {
        const allSports = this.allSports.get();
        if (allSports.type === 'ready') {
            return allSports.value;
        }
        return [];
    }

    public get sportListOptions(): Array<SportListOptionType> {
        const temp = this.sportList.map((elem) => {
            return {
                key: elem.id,
                name: elem.name,
            };
        });

        return [
            {
                key: '',
                name: 'Select sport',
            },
            ...temp,
        ];
    }
}
